// The classifier is shared by Node operator tools, the Workers API, and the browser console.
// Keep it runtime-neutral: URL performs the same IDNA normalization without a node:url import.
import { parse as parseDomain } from 'tldts';

export const READINESS_SCHEMA_VERSION = 1;

export const CLOUD_RESULTS = Object.freeze({
  qualifies: 'qualifies',
  changesRequired: 'changes_required',
  manualReview: 'manual_review',
});

export const ORANGE_TO_ORANGE_PROFILE = 'customer-controlled-cloudflare-orange-to-orange';

export const HOSTED_EDGE_COMPATIBILITY_PROFILES = Object.freeze({
  [ORANGE_TO_ORANGE_PROFILE]: Object.freeze({
    id: ORANGE_TO_ORANGE_PROFILE,
    summary:
      'The customer controls the current Cloudflare zone, can repoint the exact hostname, and exposes a separate direct origin.',
    requiredAuthority: Object.freeze([
      'customerControlsCurrentZone',
      'canRepointExactHostname',
      'directOrigin',
    ]),
  }),
});

const EVIDENCE_STATES = new Set(['confirmed', 'unavailable', 'unknown']);
const APEX_STRATEGIES = new Set(['flattening', 'none', 'unknown']);
const ORIGIN_REACHABILITY = new Set(['reachable', 'unreachable', 'unknown']);
const PROXY_LAYERS = new Set(['none', 'documented', 'conflict', 'unknown']);

const REASONS = Object.freeze({
  dns_control_unavailable: {
    message: 'The protected hostname cannot currently be changed at its authoritative DNS.',
    unlock: {
      code: 'obtain_dns_control',
      message: 'Obtain authority to change the protected hostname, or continue with Guard Dog.',
    },
  },
  dns_control_unknown: {
    message: 'DNS control for the protected hostname has not been confirmed.',
    unlock: {
      code: 'confirm_dns_control',
      message: 'Confirm who can change the protected hostname at its authoritative DNS.',
    },
  },
  apex_strategy_missing: {
    message: 'The protected hostname is an apex without confirmed flattening or ALIAS support.',
    unlock: {
      code: 'choose_apex_path',
      message:
        'Confirm flattening or ALIAS support for this exact apex, or check its www replacement separately.',
    },
  },
  apex_strategy_unknown: {
    message: 'The exact apex routing capability has not been confirmed.',
    unlock: {
      code: 'confirm_apex_capability',
      message:
        'Confirm whether the authoritative DNS supports flattening or ALIAS for this exact apex.',
    },
  },
  apex_replacement_requires_new_check: {
    message:
      'A replacement hostname cannot qualify the apex using evidence collected for the apex.',
    unlock: {
      code: 'check_replacement_hostname',
      message:
        'Run a fresh readiness check for the exact replacement hostname, then prove the apex redirect separately.',
    },
  },
  origin_unreachable: {
    message: 'The declared site-serving origin is not currently reachable over HTTP or HTTPS.',
    unlock: {
      code: 'expose_stable_origin',
      message: 'Expose and verify a stable public HTTP or HTTPS origin for the protected site.',
    },
  },
  origin_reachability_unknown: {
    message: 'Reachability of the declared site-serving origin has not been established.',
    unlock: {
      code: 'check_origin_reachability',
      message: 'Run the bounded origin probe and retain its status and timing evidence.',
    },
  },
  origin_identity_unavailable: {
    message: 'The declared origin is known not to serve the protected site.',
    unlock: {
      code: 'identify_site_origin',
      message: 'Provide a stable origin that serves the protected site for its host.',
    },
  },
  origin_identity_unknown: {
    message: 'The declared origin has not been confirmed as the origin that serves this site.',
    unlock: {
      code: 'confirm_origin_identity',
      message: 'Confirm that the declared origin serves the protected site for its host.',
    },
  },
  proxy_conflict: {
    message:
      'Another full proxy is in front of the protected hostname without an approved layering path.',
    unlock: {
      code: 'resolve_proxy_conflict',
      message: 'Remove the conflicting proxy or adopt a reviewed, documented layering profile.',
    },
  },
  proxy_unknown: {
    message: 'The current proxy or CDN topology has not been confirmed.',
    unlock: {
      code: 'confirm_proxy_topology',
      message: 'Confirm whether another full proxy or CDN is in front of the protected hostname.',
    },
  },
  layering_profile_unknown: {
    message: 'The declared layered topology does not name a reviewed compatibility profile.',
    unlock: {
      code: 'document_layering_profile',
      message:
        'Select a reviewed compatibility profile or document the topology before continuing.',
    },
  },
  layering_profile_unsupported: {
    message: 'The declared layered topology is not an approved hosted-edge compatibility profile.',
    unlock: {
      code: 'review_layering_profile',
      message: 'Review and document this topology before using it for hosted protection.',
    },
  },
  layering_authority_unavailable: {
    message:
      'The declared layered topology lacks one or more required control or origin capabilities.',
    unlock: {
      code: 'obtain_layering_authority',
      message:
        'Obtain the exact DNS, repointing, and direct-origin capabilities required by the profile.',
    },
  },
  layering_authority_unknown: {
    message: 'One or more required controls for the declared layering profile remain unconfirmed.',
    unlock: {
      code: 'confirm_layering_authority',
      message:
        'Confirm every DNS, repointing, and direct-origin requirement in the selected profile.',
    },
  },
  contradictory_evidence: {
    message:
      'The supplied readiness evidence contains facts that cannot describe one exact hostname topology.',
    unlock: {
      code: 'resolve_evidence_conflict',
      message:
        'Resolve the contradictory facts and run a fresh readiness check for the exact hostname.',
    },
  },
});

function readinessError(message) {
  throw new Error(`hostname readiness: ${message}`);
}

function enumValue(value, allowed, fallback, label) {
  const normalized = value ?? fallback;
  if (!allowed.has(normalized)) readinessError(`invalid ${label}`);
  return normalized;
}

function checkedAt(value) {
  if (typeof value !== 'string' || value.trim() === '') {
    readinessError('checkedAt must be an explicit timestamp');
  }
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) readinessError('checkedAt must be a valid timestamp');
  return date.toISOString();
}

function optionalRecord(value, label) {
  if (value === undefined || value === null) return {};
  if (typeof value !== 'object' || Array.isArray(value)) {
    readinessError(`${label} must be an object`);
  }
  return value;
}

export function normalizeReadinessHostname(value) {
  if (typeof value !== 'string' || value.trim() === '') readinessError('hostname is required');

  const raw = value.trim().replace(/\.+$/, '');
  if (/[\s/:?#@]/u.test(raw))
    readinessError('hostname must not include a URL, port, or credentials');

  let normalized;
  try {
    normalized = new URL(`http://${raw}/`).hostname.toLowerCase();
  } catch {
    readinessError('hostname is not a valid public name');
  }
  if (!normalized || normalized.length > 253) readinessError('hostname is not a valid public name');
  const labels = normalized.split('.');
  if (
    labels.some(
      (label) =>
        label.length < 1 ||
        label.length > 63 ||
        label.startsWith('-') ||
        label.endsWith('-') ||
        !/^[a-z0-9-]+$/.test(label),
    )
  ) {
    readinessError('hostname is not a valid public name');
  }

  const parsed = parseDomain(normalized, { allowPrivateDomains: true });
  if (
    !parsed.domain ||
    parsed.isIp ||
    !parsed.publicSuffix ||
    (!parsed.isIcann && !parsed.isPrivate)
  ) {
    readinessError('hostname must have a registrable public domain');
  }

  return {
    normalized,
    shape: normalized === parsed.domain ? 'apex' : 'subdomain',
    registrableDomain: parsed.domain,
  };
}

function normalizeOptionalHostname(value, label) {
  if (value === undefined || value === null || value === '') return null;
  try {
    return normalizeReadinessHostname(value).normalized;
  } catch (error) {
    readinessError(`${label} is invalid: ${error.message.replace(/^hostname readiness: /, '')}`);
  }
}

function addReason(target, code) {
  if (!target.includes(code)) target.push(code);
}

function classifyEvidence(input, hostname) {
  const dnsInput = optionalRecord(input.dns, 'dns');
  const originInput = optionalRecord(input.origin, 'origin');
  const proxyInput = optionalRecord(input.proxy, 'proxy');
  const authorityInput = optionalRecord(proxyInput.authority, 'proxy.authority');
  const dns = {
    control: enumValue(dnsInput.control, EVIDENCE_STATES, 'unknown', 'dns.control'),
    apexStrategy: enumValue(dnsInput.apexStrategy, APEX_STRATEGIES, 'unknown', 'dns.apexStrategy'),
    replacementHostname: normalizeOptionalHostname(
      dnsInput.replacementHostname,
      'dns.replacementHostname',
    ),
  };
  const origin = {
    reachability: enumValue(
      originInput.reachability,
      ORIGIN_REACHABILITY,
      'unknown',
      'origin.reachability',
    ),
    servesSite: enumValue(originInput.servesSite, EVIDENCE_STATES, 'unknown', 'origin.servesSite'),
  };
  const proxy = {
    layer: enumValue(proxyInput.layer, PROXY_LAYERS, 'unknown', 'proxy.layer'),
    layeringProfile: proxyInput.layeringProfile ?? null,
    authority: {
      customerControlsCurrentZone: enumValue(
        authorityInput.customerControlsCurrentZone,
        EVIDENCE_STATES,
        'unknown',
        'proxy.authority.customerControlsCurrentZone',
      ),
      canRepointExactHostname: enumValue(
        authorityInput.canRepointExactHostname,
        EVIDENCE_STATES,
        'unknown',
        'proxy.authority.canRepointExactHostname',
      ),
      directOrigin: enumValue(
        authorityInput.directOrigin,
        EVIDENCE_STATES,
        'unknown',
        'proxy.authority.directOrigin',
      ),
    },
  };

  if (proxy.layeringProfile !== null && typeof proxy.layeringProfile !== 'string') {
    readinessError('proxy.layeringProfile must be a string');
  }

  const changes = [];
  const unknowns = [];
  const contradictions = [];

  if (dns.control === 'unavailable') addReason(changes, 'dns_control_unavailable');
  if (dns.control === 'unknown') addReason(unknowns, 'dns_control_unknown');

  if (hostname.shape === 'apex') {
    if (dns.replacementHostname) {
      addReason(changes, 'apex_replacement_requires_new_check');
      if (
        dns.replacementHostname !== `www.${hostname.normalized}` ||
        dns.apexStrategy === 'flattening'
      ) {
        addReason(contradictions, 'contradictory_evidence');
      }
    } else if (dns.apexStrategy === 'none') {
      addReason(changes, 'apex_strategy_missing');
    } else if (dns.apexStrategy === 'unknown') {
      addReason(unknowns, 'apex_strategy_unknown');
    }
  } else if (dns.apexStrategy !== 'unknown' || dns.replacementHostname) {
    addReason(contradictions, 'contradictory_evidence');
  }

  if (origin.reachability === 'unreachable') addReason(changes, 'origin_unreachable');
  if (origin.reachability === 'unknown') addReason(unknowns, 'origin_reachability_unknown');
  if (origin.servesSite === 'unavailable') addReason(changes, 'origin_identity_unavailable');
  if (origin.servesSite === 'unknown') addReason(unknowns, 'origin_identity_unknown');

  if (proxy.layer === 'conflict') addReason(changes, 'proxy_conflict');
  if (proxy.layer === 'unknown') addReason(unknowns, 'proxy_unknown');

  const authorityValues = Object.values(proxy.authority);
  if (proxy.layer === 'documented') {
    if (!proxy.layeringProfile) {
      addReason(unknowns, 'layering_profile_unknown');
    } else if (!Object.hasOwn(HOSTED_EDGE_COMPATIBILITY_PROFILES, proxy.layeringProfile)) {
      addReason(changes, 'layering_profile_unsupported');
    } else {
      const requiredAuthority = HOSTED_EDGE_COMPATIBILITY_PROFILES[
        proxy.layeringProfile
      ].requiredAuthority.map((name) => proxy.authority[name]);
      if (requiredAuthority.includes('unavailable')) {
        addReason(changes, 'layering_authority_unavailable');
      }
      if (requiredAuthority.includes('unknown')) addReason(unknowns, 'layering_authority_unknown');
    }
  } else if (
    proxy.layeringProfile !== null ||
    authorityValues.some((value) => value !== 'unknown')
  ) {
    addReason(contradictions, 'contradictory_evidence');
  }

  const reasonCodes = contradictions.length
    ? contradictions
    : changes.length
      ? [...changes, ...unknowns]
      : unknowns;
  const result = contradictions.length
    ? CLOUD_RESULTS.manualReview
    : changes.length
      ? CLOUD_RESULTS.changesRequired
      : unknowns.length
        ? CLOUD_RESULTS.manualReview
        : CLOUD_RESULTS.qualifies;

  return { dns, origin, proxy, result, reasonCodes };
}

export function classifyHostnameReadiness(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    readinessError('input must be an object');
  }

  const hostname = normalizeReadinessHostname(input.hostname);
  const evidence = classifyEvidence(input, hostname);
  const reasons = evidence.reasonCodes.map((code) => ({ code, message: REASONS[code].message }));
  const unlockSteps = [];
  const seenUnlocks = new Set();
  for (const code of evidence.reasonCodes) {
    const unlock = REASONS[code].unlock;
    if (!seenUnlocks.has(unlock.code)) {
      seenUnlocks.add(unlock.code);
      unlockSteps.push({ ...unlock });
    }
  }

  return {
    schemaVersion: READINESS_SCHEMA_VERSION,
    checkedAt: checkedAt(input.checkedAt),
    hostname,
    guardDog: { available: true },
    guardDogCloud: {
      result: evidence.result,
      reasons,
      unlockSteps,
    },
    evidence: {
      dns: evidence.dns,
      origin: evidence.origin,
      proxy: evidence.proxy,
    },
  };
}

export function renderHostnameReadiness(report) {
  if (report?.schemaVersion !== READINESS_SCHEMA_VERSION || !report.hostname?.normalized) {
    readinessError('cannot render an invalid report');
  }

  const lines = ['Guard Dog is available for this WordPress site.'];
  if (report.guardDogCloud?.result === CLOUD_RESULTS.qualifies) {
    lines.push(`Guard Dog Cloud qualifies for hosted protection on ${report.hostname.normalized}.`);
    lines.push(`Next: proceed to operator onboarding for ${report.hostname.normalized}.`);
    return lines.join('\n');
  }

  if (report.guardDogCloud?.result === CLOUD_RESULTS.changesRequired) {
    lines.push(
      `Guard Dog Cloud needs changes before hosted protection can cover ${report.hostname.normalized}.`,
    );
  } else if (report.guardDogCloud?.result === CLOUD_RESULTS.manualReview) {
    lines.push(
      `Guard Dog Cloud needs a manual review before hosted protection can cover ${report.hostname.normalized}.`,
    );
  } else {
    readinessError('cannot render an unknown Guard Dog Cloud result');
  }

  if (report.guardDogCloud.reasons.length) {
    lines.push('Why:');
    for (const reason of report.guardDogCloud.reasons) lines.push(`- ${reason.message}`);
  }
  if (report.guardDogCloud.unlockSteps.length) {
    lines.push('Next steps:');
    for (const step of report.guardDogCloud.unlockSteps) lines.push(`- ${step.message}`);
  }
  return lines.join('\n');
}
